import SectionPanel from '@/components/SectionPanel';
import { Box, Fieldset, Input, Stack, Text } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import type { Organization } from '@/api/Organization';
import { useAuthContext } from '@/auth/AuthContext';
import RequireAdmin from '@/auth/RequireAdmin';
import Content from '@/components/Content';
import FormActions from '@/components/FormActions';
import WaitingSpinner from '@/components/WaitingSpinner';
import SubmitButton from '@/components/buttons/SubmitButton';
import CountrySelect from '@/components/fields/CountrySelect';
import { FormInput } from '@/components/fields/FormInput';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { countries } from '@/data/Countries';
import { useI18n } from '@/i18n/I18nContext';
import { organizationService, type SettingsEntry } from '@/service/OrganizationService';

const toEntry = (organization: Organization): SettingsEntry => ({
    name: organization.name, country: organization.country, city: organization.city ?? '',
});

const SettingsForm = ({ organization, onSaved }: {
    organization: Organization;
    onSaved: (organization: Organization) => void;
}) => {
    const { t, language } = useI18n();
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const active = useRef(false);
    useEffect(() => {
        active.current = true;
        return () => { active.current = false; };
    }, []);
    const { register, control, getValues, setValue, handleSubmit, reset, formState: { errors, isDirty, isSubmitting } } = useForm<SettingsEntry>({
        defaultValues: toEntry(organization),
    });

    const onSubmit = handleSubmit(async entry => {
        setError('');
        setSaved(false);
        try {
            const updated = await organizationService.updateSettings(entry);
            if (!active.current) return;
            if (updated.id !== organization.id) throw new Error(t('organizationSettings.saveError'));
            reset(toEntry(updated));
            setSaved(true);
            onSaved(updated);
        } catch (cause) {
            if (active.current) setError(cause instanceof Error ? cause.message : t('organizationSettings.saveError'));
        }
    });

    return (
        <form noValidate onSubmit={onSubmit} onChange={() => setSaved(false)}>
            <Stack gap='6' minW='0'>
                <SectionPanel title={t('organizationSettings.information')}>
                    <Fieldset.Root disabled={isSubmitting}>
                        <Fieldset.Content gap='4'>
                            <FormInput name='name' label={t('organizationSettings.name')} register={register} error={errors.name} required
                                rules={{ validate: value => value.trim().length > 0 || t('organizationSettings.nameRequired') }} />
                            <CountrySelect name='country' control={control} readOnly={isSubmitting} isDisabled={isSubmitting}
                                countryCodes={Object.keys(countries)} language={language}
                                label={t('common.country.label')} requiredMessage={t('common.country.required')}
                                placeholder={t('common.country.placeholder')} inputId='settings-country'
                                aria-label={t('common.country.label')} isClearable={false}
                                onValueChange={country => {
                                    setSaved(false);
                                    if (country !== getValues('country')) setValue('city', '', { shouldDirty: true });
                                }} />
                            <Field label={t('organizationSettings.city')} optionalText={t('organizationSettings.optional')}>
                                <Input autoComplete='address-level2' {...register('city')} />
                            </Field>
                        </Fieldset.Content>
                    </Fieldset.Root>
                </SectionPanel>
                <SectionPanel title={t('organizationSettings.currencySection')}>
                    <Text fontWeight='semibold'>{t('organizationSettings.currency')}: {organization.currency}</Text>
                    <Text fontSize='sm' color='fg.muted'>{t('organizationSettings.currencyNote')}</Text>
                </SectionPanel>
                {error && <Alert status='error' title={t('organizationSettings.saveError')}>{error}</Alert>}
                {saved && <Text role='status' color='green.600'>{t('organizationSettings.saved')}</Text>}
                <Text fontSize='sm' color='fg.muted'>{t('organizationSettings.reloadHint')}</Text>
                <FormActions>
                    <Button type='button' variant='outline' colorPalette='gray' disabled={!isDirty || isSubmitting}
                        onClick={() => { reset(toEntry(organization)); setError(''); setSaved(false); }}>
                        {t('organizationSettings.reset')}
                    </Button>
                    <SubmitButton isSubmitting={isSubmitting} disabled={!isDirty || isSubmitting} />
                </FormActions>
            </Stack>
        </form>
    );
};

const SettingsContent = ({ token, organizationId }: { token: string; organizationId: number }) => {
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasLoadError, setHasLoadError] = useState(false);
    const [loadVersion, setLoadVersion] = useState(0);
    const { t } = useI18n();
    const navigate = useNavigate();

    useEffect(() => {
        let active = true;
        setIsLoading(true);
        setHasLoadError(false);
        organizationService.current(token).then(value => {
            if (!active) return;
            if (value.id !== organizationId) throw new Error('Organization does not match the current session');
            setOrganization(value);
        }).catch(() => {
            if (active) setHasLoadError(true);
        }).finally(() => {
            if (active) setIsLoading(false);
        });
        return () => { active = false; };
    }, [token, organizationId, loadVersion]);

    return (
        <Content title={t('organizationSettings.title')} description={t('organizationSettings.description')}
            actions={<Button variant='outline' onClick={() => navigate('/organization')}>
                    {t('organizationSettings.back')}
            </Button>}>
            <Stack gap='6' maxW='3xl' mx='auto' minW='0'>
                {organization ? <SettingsForm organization={organization} onSaved={updated => {
                    setOrganization(updated);
                    navigate(0);
                }} /> : isLoading ? (
                    <Box role='status' aria-label={t('organizationSettings.loading')}><WaitingSpinner size='md' /></Box>
                ) : hasLoadError ? (
                    <>
                        <Alert status='error' title={t('organizationSettings.loadError')} />
                        <Button onClick={() => setLoadVersion(value => value + 1)}>{t('organizationSettings.retry')}</Button>
                    </>
                ) : null}
            </Stack>
        </Content>
    );
};

const OrganizationSettings = () => {
    const { user } = useAuthContext();
    return <RequireAdmin>{user && <SettingsContent key={`${user.data.organizationId}:${user.token}`} token={user.token} organizationId={user.data.organizationId} />}</RequireAdmin>;
};

export default OrganizationSettings;
